<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import * as rutHelpers from 'rut-helpers';
import { usersApi, type User } from '@/api/users';
import { rolesApi, type Role } from '@/api/roles';
import { useUser } from '@/composables/useUser';
import { requestErrorMessage } from '@/utils/api-errors';

interface Props {
  user?: User;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
  success: [];
}>();

const { user: currentUser, setCurrentUser } = useUser();
const isEditing = computed(() => !!props.user);
const isSelf = computed(() => isEditing.value && props.user?.id === currentUser?.value?.id);
const isActive = ref(props.user?.isActive ?? true);

const queryClient = useQueryClient();

const validationSchema = yup.object({
  email: yup
    .string()
    .required('El email es requerido')
    .email('Debe ser un email válido'),
  roleId: yup
    .number()
    .required('El rol es requerido'),
  // The name and RUT the user signs registros clínicos with.
  firstName: yup.string().nullable(),
  lastName: yup.string().nullable(),
  dni: yup.string().nullable()
    .test('valid-dni', 'El RUT ingresado no es válido', (value) => (value ? rutHelpers.rutValidate(value) : true)),
});

const { values, errors, defineField, handleSubmit, meta } = useForm({
  validationSchema,
  initialValues: {
    email: props.user?.email || '',
    roleId: props.user?.role?.id as number | undefined,
    firstName: props.user?.firstName ?? '',
    lastName: props.user?.lastName ?? '',
    dni: props.user?.dni ? rutHelpers.rutFormat(props.user.dni) : '',
  },
});

const [email, emailAttrs] = defineField('email');
const [roleId, roleIdAttrs] = defineField('roleId');
const [firstName, firstNameAttrs] = defineField('firstName');
const [lastName, lastNameAttrs] = defineField('lastName');
const [dni, dniAttrs] = defineField('dni');

watch(dni, (value) => {
  if (value) dni.value = rutHelpers.rutFormat(value);
});

function identity() {
  return { firstName: values.firstName ?? '', lastName: values.lastName ?? '', dni: values.dni ?? '' };
}

const { data: assignableRoles } = useQuery({
  queryKey: ['roles', 'assignable'],
  queryFn: () => rolesApi.index({ assignable: true }),
});

// The server refuses to edit a user whose role holds permissions the current user
// lacks; such a role is never assignable, so its absence from the list locks the form
// (it is still listed so the picker can show its name).
const isOutsideCeiling = computed(() => {
  const targetRole = props.user?.role;
  if (!targetRole || !assignableRoles.value) return false;

  return !assignableRoles.value.some(role => role.id === targetRole.id);
});
const roles = computed(() => {
  const targetRole = props.user?.role;

  return isOutsideCeiling.value && targetRole ?
    [...(assignableRoles.value ?? []), targetRole] :
    assignableRoles.value;
});
const isLocked = computed(() => isSelf.value || isOutsideCeiling.value);
// A user gives their own name and RUT once; after that an admin corrects them.
const identityLocked = computed(() => isOutsideCeiling.value || (isSelf.value && !!props.user?.clinicalIdentity));

const createMutation = useMutation({
  mutationFn: () => usersApi.createInternal({ email: values.email, roleId: values.roleId!, ...identity() }),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
    emit('success');
    emit('close');
  },
});

// The user may only change their own name and RUT, through their profile.
const updateMutation = useMutation({
  mutationFn: () => {
    if (!props.user?.id) throw new Error('User ID is required for update');
    if (isSelf.value) return usersApi.updateProfile(identity());

    return usersApi.update(props.user.id, { roleId: values.roleId, isActive: isActive.value, ...identity() });
  },
  onSuccess: (user) => {
    if (isSelf.value) setCurrentUser?.(user);
    queryClient.invalidateQueries({ queryKey: ['users'] });
    emit('success');
    emit('close');
  },
});

const isLoading = computed(() => createMutation.isPending.value || updateMutation.isPending.value);
const isError = computed(() => createMutation.isError.value || updateMutation.isError.value);
const updateErrorMessage = computed(() => requestErrorMessage(
  updateMutation.error.value,
  'Ha ocurrido un error al actualizar el usuario. Intenta nuevamente.',
));

const onSubmit = handleSubmit(() => {
  if (isEditing.value) {
    updateMutation.mutate();
  } else {
    createMutation.mutate();
  }
});

function handleClose() {
  emit('close');
}
</script>
<template>
  <v-card>
    <v-card-title>{{ isEditing ? 'Editar Usuario' : 'Nuevo Usuario' }}</v-card-title>
    <v-card-text>
      <form @submit.prevent="onSubmit">
        <v-text-field
          v-model="email"
          label="Email"
          :error-messages="errors.email"
          :disabled="isLoading || isEditing"
          :readonly="isEditing"
          variant="outlined"
          class="mb-4"
          type="email"
          v-bind="emailAttrs"
        />

        <v-row dense>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model="firstName"
              v-bind="firstNameAttrs"
              label="Nombre"
              :error-messages="errors.firstName"
              :disabled="isLoading || identityLocked"
              autocomplete="off"
              variant="outlined"
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model="lastName"
              v-bind="lastNameAttrs"
              label="Apellido"
              :error-messages="errors.lastName"
              :disabled="isLoading || identityLocked"
              autocomplete="off"
              variant="outlined"
            />
          </v-col>
        </v-row>
        <v-text-field
          v-model="dni"
          v-bind="dniAttrs"
          label="RUT"
          :hint="identityLocked && isSelf
            ? 'Ya registraste tu nombre y RUT: pide a un administrador que los corrija.'
            : 'Nombre y RUT identifican a quien registra en la ficha clínica.'"
          persistent-hint
          :error-messages="errors.dni"
          :disabled="isLoading || identityLocked"
          variant="outlined"
          class="mb-4"
        />

        <v-autocomplete
          v-model="roleId"
          :items="roles"
          :item-title="(role: Role) => role.readableName || role.name"
          item-value="id"
          label="Rol"
          :error-messages="errors.roleId"
          :disabled="isLoading || isLocked"
          :readonly="isLocked"
          variant="outlined"
          class="mb-4"
          v-bind="roleIdAttrs"
        />

        <v-switch
          v-if="isEditing && !isLocked"
          v-model="isActive"
          :label="isActive ? 'Activo' : 'Inactivo'"
          color="primary"
          :disabled="isLoading"
          hide-details
          class="mb-4"
        />
      </form>

      <v-alert
        v-if="isOutsideCeiling"
        type="info"
        variant="tonal"
        class="mt-2"
      >
        No puedes editar este usuario porque su rol tiene permisos que tú no tienes.
      </v-alert>

      <v-alert
        v-if="isError"
        type="error"
        variant="tonal"
        class="mt-2"
      >
        {{ isEditing
          ? updateErrorMessage
          : 'Ha ocurrido un error. Intenta nuevamente. Verifica que el email no esté registrado.'
        }}
      </v-alert>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="isLoading"
        @click="handleClose"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        :disabled="!meta.valid || isOutsideCeiling || (isSelf && identityLocked)"
        :loading="isLoading"
        @click="onSubmit"
      >
        {{ isEditing ? 'Guardar' : 'Crear' }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
